import { SortConfig, VirtualTable } from '@components/ui/VirtualTable';
import { Badge } from '@shared/components/ui/badge';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Client } from '../../models/Clients';

interface TableProps {
  clients: Client[];
  isLoading?: boolean;
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
  selectedColumns?: string[];
  refetch?: () => void;
  // Props de selección
  selectedClients?: Set<number>;
  onToggleClientSelection?: (client: Client) => void;
  onSelectAllClients?: () => void;
  onClearSelection?: () => void;
}

export const Table = React.memo(function Table({
  clients,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  selectedColumns,
  refetch,
  selectedClients,
  onToggleClientSelection,
  onSelectAllClients,
}: TableProps) {
  const { t } = useTranslation('clientsModule');

  const areAllSelected = useMemo(
    () => clients.length > 0 && selectedClients?.size === clients.length,
    [clients.length, selectedClients?.size],
  );

  const columns = useMemo<ColumnDef<Client>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('table.columns.name'),
        cell: ({ getValue }) => <div className="font-medium text-foreground">{getValue() as string}</div>,
      },
      {
        id: 'user.name',
        accessorFn: (row) => row.user.name,
        header: t('table.columns.userName'),
        cell: ({ getValue }) => <div className="text-foreground">{getValue() as string}</div>,
      },
      {
        id: 'user.email',
        accessorFn: (row) => row.user.email,
        header: t('table.columns.email'),
        cell: ({ getValue }) => <div className="text-muted-foreground">{getValue() as string}</div>,
      },
      {
        id: 'user.phone',
        accessorFn: (row) => row.user.phone,
        header: t('table.columns.phone'),
        cell: ({ getValue }) => <div className="text-foreground">{(getValue() as string) || '-'}</div>,
      },
      {
        id: 'country',
        accessorKey: 'country',
        header: t('table.columns.country'),
        cell: ({ getValue }) => (
          <Badge variant="outline" className="font-mono">
            {getValue() as string}
          </Badge>
        ),
      },
      {
        id: 'city',
        accessorKey: 'city',
        header: t('table.columns.city'),
        cell: ({ getValue }) => <div className="text-foreground">{getValue() as string}</div>,
      },
    ],
    [t],
  );

  const visibleColumns = useMemo(
    () => (selectedColumns ? columns.filter((col) => selectedColumns.includes(col.id as string)) : columns),
    [columns, selectedColumns],
  );

  return (
    <VirtualTable
      data={clients}
      columns={visibleColumns}
      sorting={sorting}
      onSortingChange={onSortingChange}
      onLoadMore={fetchNextPage}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      enableInfiniteScroll={true}
      enableSelection={true}
      selectedItems={selectedClients}
      onRowSelectionChange={(client) => onToggleClientSelection?.(client)}
      onSelectAll={onSelectAllClients}
      isAllSelected={areAllSelected}
      className="transition-colors"
    />
  );
});
